import React from "react";
import { useTranslation } from "react-i18next";

/* =========================================================
   ICONS
========================================================= */

function StepIcon({ type }) {
  if (type === "user-plus") {
    return (
      <svg viewBox="0 0 64 64" fill="none">
        <circle cx="27" cy="20" r="9" />
        <path d="M10 48C10 39.7 17.2 33 26 33C34.8 33 42 39.7 42 48" />
        <path d="M48 25V39" />
        <path d="M41 32H55" />
      </svg>
    );
  }

  if (type === "mail") {
    return (
      <svg viewBox="0 0 64 64" fill="none">
        <rect x="8" y="14" width="48" height="36" rx="5" />
        <path d="M10 18L32 35L54 18" />
        <path d="M10 48L25 34" />
        <path d="M54 48L39 34" />
      </svg>
    );
  }

  if (type === "login") {
    return (
      <svg viewBox="0 0 64 64" fill="none">
        <path d="M35 10H49C52.3 10 55 12.7 55 16V48C55 51.3 52.3 54 49 54H35" />
        <path d="M30 20L42 32L30 44" />
        <path d="M42 32H9" />
      </svg>
    );
  }

  if (type === "shield") {
    return (
      <svg viewBox="0 0 64 64" fill="none">
        <path d="M32 7L51 14V29C51 41.5 43.1 51.5 32 57C20.9 51.5 13 41.5 13 29V14L32 7Z" />
        <path d="M22 31L28 37L42 23" />
      </svg>
    );
  }

  return null;
}


/* =========================================================
   ARROW
========================================================= */

function StepArrow() {
  return (
    <div className="employee-step-arrow">
      <svg viewBox="0 0 40 30" fill="none">
        <path d="M6 5L20 23L34 5" />
      </svg>
    </div>
  );
}


/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function Whlic() {
  const { t } = useTranslation("help");

  const steps = [
    {
      number: "1",
      title: t("Whlic.step1Title", "Admin Adds Employee"),
      description: t(
        "Whlic.step1Description",
        "The admin can add an employee by entering the employee's registered email address."
      ),
      icon: "user-plus",
    },
    {
      number: "2",
      title: t("Whlic.step2Title", "Temporary Password"),
      description: t(
        "Whlic.step2Description",
        "Once the employee is added, a temporary password will be sent to the registered email address."
      ),
      icon: "mail",
    },
    {
      number: "3",
      title: t("Whlic.step3Title", "Login with Temporary Password"),
      description: t(
        "Whlic.step3Description",
        "The user can log in using their registered email address and the temporary password received by email."
      ),
      icon: "login",
    },
    {
      number: "4",
      title: t("Whlic.step4Title", "OTP Verification"),
      description: t(
        "Whlic.step4Description",
        "An OTP will be sent to the registered email address. Once the user enters the correct OTP, the login will be completed."
      ),
      icon: "shield",
    },
  ];

  return (
    <>
      <div className="employee-login-page">

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="employee-login-header">

          <div className="employee-header-icon">
            <StepIcon type="user-plus" />
          </div>

          <div className="employee-header-content">

            <h1>
              {t("Whlic.headerTitle", "Employee Login & Verification")}
            </h1>

            <p>
              {t(
                "Whlic.headerDescription",
                "Follow the steps below to add an employee and complete the login process."
              )}
            </p>

          </div>

        </div>


        {/* =================================================
            STEPS
        ================================================= */}

        <div className="employee-login-container">

          {steps.map((step, index) => (
            <React.Fragment key={step.number}>

              <div className="employee-step-card">

                {/* Icon */}

                <div className="employee-step-icon">

                  <StepIcon type={step.icon} />

                </div>


                {/* Content */}

                <div className="employee-step-content">

                  <h2>
                    <span>{step.number}.</span>{" "}
                    {step.title}
                  </h2>

                  <p>
                    {step.description}
                  </p>

                </div>

              </div>


              {/* Arrow */}

              {index < steps.length - 1 && (
                <StepArrow />
              )}

            </React.Fragment>
          ))}

        </div>

      </div>


      {/* =====================================================
          CSS
      ===================================================== */}

      <style>{`

        /* =================================================
           MAIN PAGE
        ================================================= */

        .employee-login-page {
          width: 100%;
          min-height: 100vh;

          box-sizing: border-box;

          padding: 32px 5% 50px;

          background:
            radial-gradient(
              circle at 50% 0%,
              rgba(0, 83, 180, 0.08),
              transparent 40%
            ),
            #0F172A;

          color: #ffffff;

          
        }


        /* =================================================
           HEADER
        ================================================= */

        .employee-login-header {
          width: 100%;
          max-width: 1540px;

          margin: 0 auto 35px;

          display: flex;
          align-items: center;

          gap: 30px;
        }


        /* =================================================
           HEADER ICON
        ================================================= */

        .employee-header-icon {
          width: 105px;
          height: 105px;

          min-width: 105px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 23px;

          background:
            linear-gradient(
              145deg,
              #063B82,
              #08285A
            );

          border: 2px solid #075FC9;

          box-shadow:
            0 0 25px rgba(0, 102, 255, 0.12),
            inset 0 0 25px rgba(0, 105, 255, 0.15);
        }


        .employee-header-icon svg {
          width: 62px;
          height: 62px;

          stroke: #54ADFF;

          stroke-width: 2.7;

          stroke-linecap: round;
          stroke-linejoin: round;
        }


        /* =================================================
           HEADER TEXT
        ================================================= */

        .employee-header-content h1 {
          margin: 0 0 10px;

         font-size:17px;

          line-height: 1.2;

          font-weight: bold;

          color: #ffffff;
        }


        .employee-header-content p {
          margin: 0;

       

          line-height: 1.5;

          color: #8EB7E8;
        }


        /* =================================================
           CONTAINER
        ================================================= */

        .employee-login-container {
          width: 100%;
          max-width: 1540px;

          margin: 0 auto;
        }


        /* =================================================
           STEP CARD
        ================================================= */

        .employee-step-card {
          width: 100%;

          min-height: 148px;

          box-sizing: border-box;

          display: flex;
          align-items: center;

          padding: 22px 34px;

          border-radius: 22px;

          border: 2px solid #075CC2;

          background:
            linear-gradient(
              110deg,
              rgba(6, 35, 73, 0.92),
              rgba(6, 27, 58, 0.82)
            );

          box-shadow:
            0 5px 18px rgba(0, 0, 0, 0.18),
            inset 0 0 30px rgba(0, 91, 190, 0.04);

          transition:
            border-color 0.25s ease,
            transform 0.25s ease,
            box-shadow 0.25s ease;
        }


        /* =================================================
           HOVER
        ================================================= */

        .employee-step-card:hover {
          border-color: #1283FF;

          transform: translateY(-2px);

          box-shadow:
            0 8px 25px rgba(0, 0, 0, 0.25),
            0 0 20px rgba(0, 102, 255, 0.08);
        }


        /* =================================================
           STEP ICON
        ================================================= */

        .employee-step-icon {
          width: 102px;
          height: 102px;

          min-width: 102px;

          margin-right: 38px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 21px;

          background:
            linear-gradient(
              145deg,
              #073C82,
              #092E65
            );

          border: 1px solid #075FC9;

          box-shadow:
            inset 0 0 20px rgba(0, 120, 255, 0.08);
        }


        .employee-step-icon svg {
          width: 61px;
          height: 61px;

          stroke: #65B5FF;

          stroke-width: 2.7;

          stroke-linecap: round;
          stroke-linejoin: round;
        }


        /* =================================================
           CONTENT
        ================================================= */

        .employee-step-content {
          flex: 1;

          min-width: 0;
        }


        .employee-step-content h2 {
          margin: 0 0 8px;
font-size:16px;
        

          line-height: 1.3;

          font-weight: 700;

          color: #ffffff;
        }


        .employee-step-content h2 span {
          color: #ffffff;
        }


        .employee-step-content p {
          margin: 0;

          max-width: 1250px;

          font-size: 13px;

          line-height: 1.55;

          font-weight: 500;

          color: #8EB9EB;
        }


        /* =================================================
           ARROW
        ================================================= */

        .employee-step-arrow {
          height: 42px;

          display: flex;
          align-items: center;
          justify-content: center;
        }


        .employee-step-arrow svg {
          width: 34px;
          height: 27px;

          stroke: #0084FF;

          stroke-width: 3;

          stroke-linecap: round;
          stroke-linejoin: round;

          filter:
            drop-shadow(
              0 0 5px rgba(0, 125, 255, 0.3)
            );
        }


        /* =================================================
           TABLET
        ================================================= */

        @media (max-width: 1000px) {

          .employee-login-page {
            padding: 30px;
          }


          .employee-header-icon {
            width: 85px;
            height: 85px;

            min-width: 85px;
          }


          .employee-header-icon svg {
            width: 50px;
            height: 50px;
          }


          .employee-header-content h1 {
            font-size: 15px;
          }


          .employee-header-content p {
            font-size: 13px;
          }


          .employee-step-card {
            min-height: 135px;

            padding: 20px 25px;
          }


          .employee-step-icon {
            width: 40px;
            height: 40px;

            min-width: 40px;

            margin-right: 28px;
          }


          .employee-step-icon svg {
            width: 50px;
            height: 50px;
          }


          .employee-step-content h2 {
            font-size: 15px;
          }


          .employee-step-content p {
            font-size: 13px;
          }

        }


        /* =================================================
           MOBILE
        ================================================= */

        @media (max-width: 650px) {

          .employee-login-page {
            padding: 25px 15px 40px;
          }


          .employee-login-header {
            align-items: flex-start;

            gap: 18px;

            margin-bottom: 25px;
          }


          .employee-header-icon {
            width: 65px;
            height: 65px;

            min-width: 65px;

            border-radius: 16px;
          }


          .employee-header-icon svg {
            width: 40px;
            height: 40px;
          }


          .employee-header-content h1 {
           
          }


          .employee-header-content p {
            font-size: 13px;
          }


          .employee-step-card {
            flex-direction: column;

            align-items: flex-start;

            padding: 22px;

            min-height: auto;
          }


          .employee-step-icon {
            width: 70px;
            height: 70px;

            min-width: 70px;

            margin-right: 0;
            margin-bottom: 18px;

            border-radius: 16px;
          }


          .employee-step-icon svg {
            width: 42px;
            height: 42px;
          }


          .employee-step-content h2 {
            font-size: 16px;

            margin-bottom: 8px;
          }


          .employee-step-content p {
            font-size: 14px;

            line-height: 1.55;
          }


          .employee-step-arrow {
            height: 38px;
          }


          .employee-step-arrow svg {
            width: 27px;
            height: 22px;
          }

        }

      `}</style>
    </>
  );
}